/**
 * Every sentence a viewer reads, in English and Korean. **English is the default** (Tyler,
 * 2026-10-20) — unlike the dashboard, which defaults to Korean for its admins: the rerun
 * channel's audience reads English first, and the toggle is one click for everyone else.
 *
 * `Dictionary` is `typeof en`, so a string added to one language and not the other is a
 * compile error, and every interpolation is a function so a translation that drops an
 * argument does not compile either. Titles, category names and the channel login are data
 * and are never translated.
 */

const en = {
  title: "Vote for the next VOD",
  signIn: "Sign in with Twitch",
  signOut: "Sign out",
  onAir: (channel: string) => `On air now · ${channel} rerun`,
  leftInVod: "left in this VOD",
  pickNext: "Pick what plays next",
  votingSub: (votes: string) => `${votes} so far · one per Twitch account · the vote closes when this VOD ends`,
  votes: (count: number) => `${count.toLocaleString("en")} vote${count === 1 ? "" : "s"}`,
  closesIn: "Vote closes in",
  upNext: (title: string) => `Up next: ${title}`,
  wonWith: (percent: number, votes: string) => `Won with ${String(percent)}% of ${votes}`,
  startingNow: "Starting now",
  noVote: "No vote right now",
  noVoteBody: "A new vote opens when the next VOD starts. Keep this page open and it will appear here.",
  connecting: "Connecting…",
  chatHint: (channel: string) =>
    `Typing !vote 1, !vote 2 or !vote 3 in ${channel}'s chat votes for the first, second or third. It is the same vote, one per account.`,
  streamed: (date: string) => `Streamed ${date}`,
  categories: "Categories",
  leading: "Leading",
  voteFor: "Vote",
  switchTo: "Switch to this",
  yourVote: "Your vote",
  signInToVote: "Sign in to vote",
  winner: "Winner",
  youVotedThis: "You voted for this",
  upNextStamp: "UP NEXT",
  offline: "The vote is unavailable right now. This page will pick it up again on its own.",
  closed: "That vote has just closed.",
  cooldown: "Give it a moment before switching again.",
  held: "Your vote is saved. It counts as soon as the vote reconnects.",
  ended: "That vote ended before your vote reached it.",
  superseded: "You voted again in chat since, and that vote is the one that counts.",
  signInProblem: {
    unconfigured: "Signing in to vote isn't set up yet.",
    expired: "That sign-in took too long. Try again.",
    failed: "Twitch didn't complete the sign-in. Try again.",
  },
  dialog: {
    title: "Sign in with Twitch to vote",
    body: "Signing in proves you're one person with one vote. This site asks Twitch for no permissions at all.",
    weSee: "We see",
    seeItems: ["Your Twitch username", "Your display name", "Your profile picture"],
    weCannot: "We can't",
    cannotItems: ["Chat or post as you", "Follow or subscribe", "See your email", "See who you watch"],
    revoke: "You can remove access any time in Twitch → Settings → Connections.",
    notNow: "Not now",
    continue: "Continue to Twitch",
  },
  units: { d: "d", h: "h", m: "m", s: "s" },
};

export type Dictionary = typeof en;

const ko: Dictionary = {
  title: "다음 VOD 투표",
  signIn: "Twitch로 로그인",
  signOut: "로그아웃",
  onAir: (channel: string) => `지금 방송 중 · ${channel} 리런`,
  leftInVod: "남음",
  pickNext: "다음에 볼 방송을 골라 주세요",
  votingSub: (votes: string) => `지금까지 ${votes} · Twitch 계정당 한 표 · 이 VOD가 끝나면 투표가 마감됩니다`,
  votes: (count: number) => `${count.toLocaleString("ko")}표`,
  closesIn: "투표 마감까지",
  upNext: (title: string) => `다음 방송: ${title}`,
  wonWith: (percent: number, votes: string) => `${votes} 중 ${String(percent)}%로 선택됨`,
  startingNow: "곧 시작",
  noVote: "지금은 진행 중인 투표가 없습니다",
  noVoteBody: "다음 VOD가 시작되면 새 투표가 열립니다. 이 페이지를 열어 두면 여기에 바로 나타납니다.",
  connecting: "연결 중…",
  chatHint: (channel: string) =>
    `${channel} 채팅에 !vote 1, !vote 2, !vote 3을 입력하면 첫 번째, 두 번째, 세 번째 방송에 투표됩니다. 같은 투표이며 계정당 한 표입니다.`,
  streamed: (date: string) => `${date} 방송`,
  categories: "카테고리",
  leading: "1위",
  voteFor: "투표하기",
  switchTo: "이걸로 변경",
  yourVote: "내 투표",
  signInToVote: "로그인하고 투표",
  winner: "선택됨",
  youVotedThis: "내가 투표한 방송",
  upNextStamp: "다음 방송",
  offline: "지금은 투표를 불러올 수 없습니다. 페이지가 자동으로 다시 연결합니다.",
  closed: "방금 투표가 마감되었습니다.",
  cooldown: "다시 바꾸려면 잠시 기다려 주세요.",
  held: "투표가 저장되었습니다. 투표가 다시 연결되면 바로 반영됩니다.",
  ended: "투표가 반영되기 전에 해당 투표가 끝났습니다.",
  superseded: "그 후 채팅에서 다시 투표하셔서, 채팅 투표가 반영됩니다.",
  signInProblem: {
    unconfigured: "투표용 로그인이 아직 설정되지 않았습니다.",
    expired: "로그인 시간이 초과되었습니다. 다시 시도해 주세요.",
    failed: "Twitch 로그인이 완료되지 않았습니다. 다시 시도해 주세요.",
  },
  dialog: {
    title: "Twitch로 로그인하고 투표하기",
    body: "로그인은 한 사람이 한 표만 행사하도록 확인하는 용도입니다. 이 사이트는 Twitch에 어떤 권한도 요청하지 않습니다.",
    weSee: "확인하는 정보",
    seeItems: ["Twitch 아이디", "표시 이름", "프로필 사진"],
    weCannot: "할 수 없는 일",
    cannotItems: ["대신 채팅하거나 글 쓰기", "팔로우하거나 구독하기", "이메일 보기", "시청 기록 보기"],
    revoke: "Twitch → 설정 → 연결에서 언제든지 접근 권한을 해제할 수 있습니다.",
    notNow: "나중에",
    continue: "Twitch로 계속",
  },
  units: { d: "일", h: "시간", m: "분", s: "초" },
};

export type Locale = "ko" | "en";

export const DICTIONARIES: Record<Locale, Dictionary> = { ko, en };

const STORAGE_KEY = "suzyvods-lang";

/** The viewer's own choice, kept in their browser. English until they say otherwise. */
export function readLocale(): Locale {
  try {
    return localStorage.getItem(STORAGE_KEY) === "ko" ? "ko" : "en";
  } catch {
    return "en";
  }
}

export function saveLocale(locale: Locale): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // Private mode: the toggle still works for this visit.
  }
}
